import { LEVEL_CONFIG } from '../../config/level.config';
import { PLAYER_CONFIG } from '../../config/player.config';
import { RENDERING_CONFIG } from '../../config/rendering.config';
import { deriveJumpPhysics } from '../../physics/JumpPhysics';
import { doubleJumpStepReach } from '../../physics/JumpReach';
import { COLLECTIBLE_FLOAT_HEIGHT } from './segmentBuilders';

/**
 * Bottle lines that show the ideal way onto a raised top reached with a double jump (container roofs,
 * walkable pipe crowns): along the flight of the double jump that meets the face at its best moment
 * (physics/JumpReach.ts, fixed steps like the game), along the top, and down the free fall off its end
 * – at the speed the module is first met, so the line is the path a runner really takes there.
 */
export interface DeckProfile {
  /** Segment-local z of the face (the runner gets up here) and of the end (it drops off here). */
  readonly face: number;
  readonly end: number;
  /** Height of the top above the road (m). */
  readonly top: number;
}

const PHYSICS = deriveJumpPhysics(PLAYER_CONFIG);

/** Feet height of the ideal double jump onto the deck at `speed`: the face met at the best moment, the second jump in the middle of its window. */
export function doubleJumpFlight(deck: DeckProfile, speed: number): (z: number) => number {
  const reach = doubleJumpStepReach(PHYSICS, PLAYER_CONFIG.maxStepHeight, deck.top, LEVEL_CONFIG.terrain.doubleJumpStep.minTriggerWindow, RENDERING_CONFIG.fixedTimeStep);
  const trigger = reach.bestTrigger ? (reach.bestTrigger.from + reach.bestTrigger.to) / 2 : PHYSICS.jumpVelocity / PHYSICS.gravity;
  const takeOff = deck.face - PLAYER_CONFIG.collider.depth / 2 - reach.bestArrival * speed;
  const g = PHYSICS.gravity;
  return (z) => {
    const t = (z - takeOff) / speed;
    if (t <= 0) return 0;
    const first = Math.min(t, trigger);
    const y1 = PHYSICS.jumpVelocity * first - (g * first * first) / 2;
    const u = Math.max(0, t - trigger);
    return Math.max(0, y1 + PHYSICS.doubleJumpVelocity * u - (g * u * u) / 2);
  };
}

/** Seconds a runner who walks off the end falls before it lands on the road. */
export function deckFallTime(deck: DeckProfile): number {
  return Math.sqrt((2 * deck.top) / PHYSICS.gravity);
}

/** z where a runner who walks off the end at `speed` lands on the road. */
export function deckLandingZ(deck: DeckProfile, speed: number): number {
  return deck.end + deckFallTime(deck) * speed;
}

/** Bottle height along a deck line: up with the double jump, along the top, the free fall after its end. */
export function deckLineHeight(deck: DeckProfile, speed: number): (z: number) => number {
  const flight = doubleJumpFlight(deck, speed);
  return (z) => {
    if (z < deck.face) return COLLECTIBLE_FLOAT_HEIGHT + flight(z);
    if (z <= deck.end) return COLLECTIBLE_FLOAT_HEIGHT + Math.max(deck.top, flight(z));
    // Free fall from the end – where a runner who just runs off will be.
    const t = (z - deck.end) / speed;
    return COLLECTIBLE_FLOAT_HEIGHT + Math.max(0, deck.top - (PHYSICS.gravity * t * t) / 2);
  };
}
